import { Injectable, inject } from '@angular/core';
import { catchError, combineLatest, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { DeviceAuthenticationStatusService } from '@shure/shared/angular/data-access/device-authentication';
import {
	ApolloQueryError,
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	CoverageMode,
	DeviceUpdateInput,
	DeviceUserPerspective,
	LocalizationSensitivity,
	NodeChangeType,
	NodeUpdateInput,
	Placement,
	ReflectionCorrection,
	SubscriptionManagerService,
	VoiceActivityDetectionSensitivity,
	waitForGQLCompleteQueryOrThrow
} from '@shure/shared/angular/data-access/system-api/core';
import {
	mapDeviceModelFromSysApi,
	mapMeasurementUnitToSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';
import { DeviceModel, MeasurementUnit, RotationAxis } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceReadonlyStateProvider } from '@shure/shared/angular/utils/device-readonly';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { PositionPoint, PositionUnit } from '@shure/shared/angular/utils/utils-position';
import { tapOnce, noMoreSubscribers, cachedObservableById } from '@shure/shared/typescript/rxjs';

import { CoverageDeviceApiService } from '../api/coverage-device-api.service';
import { CoverageDevice, CoverageTalkerPosition } from '../api/models';
import { DeviceCoverageDeviceBehaviorService } from '../device-behavior';

import { DcaDeviceFeaturesShim } from './dca-device-features-shim';
import {
	CoverageDeviceSubscriptionGQL,
	GetCoverageDeviceQueryGQL,
	TalkerTrackingPositionSubscriptionGQL,
	UpdateCoverageDeviceMutationGQL,
	UpdateUserInterfacePreferencesMutationGQL
} from './graphql/generated/device-coverage-device.generated';
import { mapCoverageDeviceFromSysApi, mapTalkerPositionFromSysApi } from './mappers';
import { SysApiCoverageAudioChannelApiService } from './sys-api-coverage-audio-channel-api.service';
import { SysApiCoverageAudioCoverageAreaApiService } from './sys-api-coverage-audio-coverage-area-api.service';
import { SysApiCoverageAudioPassiveSpeakerApiService } from './sys-api-coverage-audio-passive-speaker-api.service';

@Injectable()
export class SysApiCoverageDeviceApiService implements CoverageDeviceApiService {
	// TODO: remove when DCA901 supports height/rotation/placement properties (DNG-36929)
	private readonly dcaFeatureShim = inject(DcaDeviceFeaturesShim);

	private readonly devicesGqlSubscriptions: CombiningSubscriptionManager;

	private readonly logger: ILogger;

	constructor(
		logger: ILogger,
		subscriptionManagerService: SubscriptionManagerService,
		coverageDeviceSubscriptionGQL: CoverageDeviceSubscriptionGQL,
		private readonly sysApiCoverageAudioChannelApiService: SysApiCoverageAudioChannelApiService,
		private readonly sysApiCoverageAudioCoverageAreaApiService: SysApiCoverageAudioCoverageAreaApiService,
		private readonly sysApiCoverageAudioPassiveSpeakerApiService: SysApiCoverageAudioPassiveSpeakerApiService,
		private readonly getCoverageDeviceQueryGQL: GetCoverageDeviceQueryGQL,
		private readonly updateCoverageDeviceMutationGQL: UpdateCoverageDeviceMutationGQL,
		private readonly updateUserInterfacePreferencesMutationGQL: UpdateUserInterfacePreferencesMutationGQL,
		private readonly talkerTrackingPositionSubscriptionGQL: TalkerTrackingPositionSubscriptionGQL,
		private readonly deviceAuthenticationStatusService: DeviceAuthenticationStatusService,
		private readonly readonlyStateProvider: DeviceReadonlyStateProvider,
		private readonly deviceBehavior: DeviceCoverageDeviceBehaviorService
	) {
		this.logger = logger.createScopedLogger('SysApiCoverageApiService');

		this.devicesGqlSubscriptions = new CombiningSubscriptionManager(
			{
				name: 'SysApiCoverageApiService:Device',
				changeTypes: [
					NodeChangeType.DeviceCoveragePosition,
					NodeChangeType.DeviceCoverageMode,
					NodeChangeType.DeviceAudioAutoFocus,
					NodeChangeType.DeviceAudioChannels,
					NodeChangeType.DeviceRotation,
					NodeChangeType.DevicePlacement,
					NodeChangeType.DeviceHeight,
					NodeChangeType.DeviceIdentify,
					NodeChangeType.DeviceTalkerTrackingConfig,
					NodeChangeType.DeviceDebugMode,
					NodeChangeType.DeviceDspDebugMode,
					NodeChangeType.DeviceCoverageLock,
					NodeChangeType.DeviceAutomixGainMetering,
					NodeChangeType.DeviceUserPerspective,
					NodeChangeType.DeviceUserInterfacePreferences,
					NodeChangeType.DeviceName,
					NodeChangeType.DeviceAllEqualizersBypass
				],
				subscriptionGql: coverageDeviceSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	@cachedObservableById()
	public subscribeTalkerTracking$(deviceId: string): Observable<CoverageTalkerPosition[]> {
		this.logger.trace('subscribeTalkerTracking$()', 'Subscribing to talker tracking', { deviceId });

		return this.talkerTrackingPositionSubscriptionGQL
			.subscribe({
				variables: {
					deviceTalkerTrackingId: deviceId
				},
				fetchPolicy: 'no-cache',
				errorPolicy: 'ignore'
			})
			.pipe(
				map((change) => {
					if (change.data && change.data.deviceTalkerTracking) {
						const result = new Array<CoverageTalkerPosition>();
						change.data.deviceTalkerTracking.talkers.forEach((talker) => {
							if ('position' in talker) {
								result.push(
									mapTalkerPositionFromSysApi(
										talker.position.confidenceLevel ?? 0,
										talker.position.xPosition,
										talker.position.yPosition,
										talker.position.zPosition
									)
								);
							}
						});
						return result;
					}
					this.logger.error('subscribeTalkerTracking$()', 'Failed to subscribe to talker tracking', {
						deviceId
					});
					return [];
				})
			);
	}

	public getDevice$(deviceId: string): Observable<CoverageDevice> {
		const queryId = uuidv4();
		this.logger.trace('getDevice$()', 'Querying for device', { queryId, deviceId });
		return combineLatest([
			this.getCoverageDeviceQueryGQL
				.watch({ variables: { id: deviceId }, errorPolicy: 'all' })
				.valueChanges.pipe(waitForGQLCompleteQueryOrThrow()),
			this.deviceAuthenticationStatusService.getDeviceState$(deviceId),
			this.readonlyStateProvider.isReadonly$(deviceId)
		]).pipe(
			map(([query, authentication, isReadonly]) => {
				if (!query.data?.node || !('isDeviceNode' in query.data.node)) {
					throw ApolloQueryErrorMapper.getError(query);
				}

				const fragment = query.data.node as Parameters<typeof mapCoverageDeviceFromSysApi>[0];
				const model = mapDeviceModelFromSysApi(fragment.description.model);
				const usePostGateValues = this.deviceBehavior.usePostGateValues(model);

				const device = mapCoverageDeviceFromSysApi(
					fragment,
					{ ...authentication, readonlyMode: isReadonly },
					usePostGateValues
				);
				this.logger.trace('getDevice$()', 'Query updated', { queryId, device });
				return device;
			}),
			tap((device) => {
				const psIds: string[] = (device.features.analogOutputChannels?.value ?? []).flatMap((c) =>
					(c.features.passiveSpeakers?.value?.passiveSpeakers ?? []).map((ps) => ps.id)
				);

				this.sysApiCoverageAudioPassiveSpeakerApiService.passiveSpeakerGqlSubscriptions.subscribeToOnly(
					queryId,
					psIds
				);
			}),
			tapOnce((device) => {
				this.devicesGqlSubscriptions.subscribe(queryId, device.id);
				const channelIds = (device.features.audioChannels?.value ?? []).map((c) => c.id);
				if (device.automixmodeManualChannelId) {
					channelIds.push(device.automixmodeManualChannelId);
				}
				this.sysApiCoverageAudioChannelApiService.audioChannelGqlSubscriptions.subscribeToOnly(
					queryId,
					channelIds
				);
				this.sysApiCoverageAudioCoverageAreaApiService.audioCoverageAreaGqlSubscriptions.subscribeToOnly(
					queryId,
					(device.features.audioCoverageAreas?.value ?? []).map((c) => c.id)
				);
			}),
			noMoreSubscribers(() => {
				this.devicesGqlSubscriptions.unsubscribeQuery(queryId);
				this.sysApiCoverageAudioChannelApiService.audioChannelGqlSubscriptions.unsubscribeQuery(queryId);
				this.sysApiCoverageAudioCoverageAreaApiService.audioCoverageAreaGqlSubscriptions.unsubscribeQuery(
					queryId
				);
				this.sysApiCoverageAudioPassiveSpeakerApiService.passiveSpeakerGqlSubscriptions.unsubscribeQuery(
					queryId
				);
			}),
			catchError((error) => {
				this.logger.error('getDevice$()', 'Query failed', { queryId, deviceId, error });
				return throwError(() => new ApolloQueryError(error));
			}),
			// TODO: remove when DCA901 supports height/rotation/placement properties (DNG-36929)
			switchMap((device) => this.mergeDcaShimmedFeatures$(device))
		);
	}

	public setDevicePosition(deviceId: string, position: PositionPoint): Observable<void> {
		this.logger.trace('setDevicePosition()', 'Setting device position', { deviceId, position });
		return this.updateCoverageDevice(deviceId, [
			{
				id: deviceId,
				features: { coveragePosition: { xPosition: position.x.rawValue, yPosition: position.y.rawValue } }
			}
		]);
	}

	public setDeviceRotation(
		deviceId: string,
		rotation: number,
		axis: RotationAxis = RotationAxis.XAxis
	): Observable<void> {
		this.logger.trace('setDeviceRotation()', 'Setting device rotation', { deviceId, rotation, axis });
		// TODO: remove when DCA901 supports rotation (DNG-36929).
		if (this.dcaFeatureShim.has(deviceId)) {
			this.dcaFeatureShim.setRotation(deviceId, axis, rotation);
			return of(undefined);
		}
		return this.updateCoverageDevice(deviceId, [{ id: deviceId, features: { rotation: { [axis]: rotation } } }]);
	}

	public setDeviceHeight(deviceId: string, height: PositionUnit): Observable<void> {
		this.logger.trace('setDeviceHeight()', 'Setting device height', { deviceId, height });
		// TODO: remove when DCA901 supports height/rotation/placement properties (DNG-36929)
		if (this.dcaFeatureShim.has(deviceId)) {
			this.dcaFeatureShim.setHeight(deviceId, height);
			return of(undefined);
		}
		return this.updateCoverageDevice(deviceId, [
			{ id: deviceId, features: { height: { height: height.rawValue } } }
		]);
	}

	public setDeviceListenerHeight(deviceId: string, listenerHeight: PositionUnit): Observable<void> {
		this.logger.trace('setDeviceListenerHeight()', 'Setting device listener height', {
			deviceId,
			height: listenerHeight
		});
		return this.updateCoverageDevice(deviceId, [
			{ id: deviceId, features: { listenerHeight: { height: listenerHeight.rawValue } } }
		]);
	}

	public setCoverageMode(deviceId: string, coverageMode: CoverageMode): Observable<void> {
		this.logger.trace('setCoverageMode()', 'Setting coverage mode', { deviceId, coverageMode });
		return this.updateCoverageDevice(deviceId, [{ id: deviceId, features: { coverageMode: { coverageMode } } }]);
	}

	public setDeviceInstallation(deviceId: string, placement: Placement): Observable<void> {
		this.logger.trace('setDeviceInstallation()', 'Setting device installation', { deviceId, placement });
		return this.updateCoverageDevice(deviceId, [{ id: deviceId, features: { placement: { placement } } }]);
	}

	public flipDevice(deviceId: string, newAngle: number): Observable<void> {
		this.logger.trace('flipDevice()', 'Flipping device', { deviceId, newAngle });
		return this.updateCoverageDevice(deviceId, [{ id: deviceId, features: { rotation: { zAxis: newAngle } } }]);
	}

	public setDeviceAudioAutoFocus(deviceId: string, enabled: boolean): Observable<void> {
		this.logger.trace('setDeviceAudioAutoFocus()', 'Setting device audio auto focus', { deviceId, enabled });
		return this.updateCoverageDevice(deviceId, [{ id: deviceId, features: { audioAutoFocus: { enabled } } }]);
	}

	public setAllEqualizersBypass(deviceId: string, bypassed: boolean): Observable<void> {
		this.logger.trace('setAllEqualizersBypass()', 'Setting all equalizers bypass', { deviceId, bypassed });
		return this.updateCoverageDevice(deviceId, [{ id: deviceId, features: { allEqualizersBypass: { bypassed } } }]);
	}

	public setMetricsIncluded(deviceId: string, metricsIncluded: boolean): Observable<void> {
		this.logger.trace('setMetricsIncluded()', 'Setting device metrics included', {
			deviceId,
			metricsIncluded
		});
		return this.updateCoverageDevice(deviceId, [
			{
				id: deviceId,
				features: {
					talkerTrackingConfig: {
						metricsIncluded
					}
				}
			}
		]);
	}

	public setLocalizationSensitivity(
		deviceId: string,
		localizationSensitivity: LocalizationSensitivity
	): Observable<void> {
		this.logger.trace('setLocalizationSensitivity()', 'Setting device localization sensitivity', {
			deviceId,
			localizationSensitivity
		});
		return this.updateCoverageDevice(deviceId, [
			{
				id: deviceId,
				features: {
					talkerTrackingConfig: {
						localizationSensitivity
					}
				}
			}
		]);
	}
	public setPositionUpdateRate(deviceId: string, positionUpdateRate: number): Observable<void> {
		this.logger.trace('setPositionUpdateRate()', 'Setting device position update ate', {
			deviceId,
			positionUpdateRate
		});
		return this.updateCoverageDevice(deviceId, [
			{
				id: deviceId,
				features: { talkerTrackingConfig: { positionUpdateRate } }
			}
		]);
	}
	public setReflectionCorrection(deviceId: string, reflectionCorrection: ReflectionCorrection): Observable<void> {
		this.logger.trace('setReflectionCorrection()', 'Setting device reflection correction', {
			deviceId,
			reflectionCorrection
		});
		return this.updateCoverageDevice(deviceId, [
			{
				id: deviceId,
				features: { talkerTrackingConfig: { reflectionCorrection } }
			}
		]);
	}
	public setVoiceActivityDetectionSensitivity(
		deviceId: string,
		voiceActivityDetectionSensitivity: VoiceActivityDetectionSensitivity
	): Observable<void> {
		this.logger.trace(
			'setVoiceActivityDetectionSensitivity()',
			'Setting device voice activity detection sensitivity',
			{
				deviceId,
				voiceActivityDetectionSensitivity
			}
		);
		return this.updateCoverageDevice(deviceId, [
			{
				id: deviceId,
				features: { talkerTrackingConfig: { voiceActivityDetectionSensitivity } }
			}
		]);
	}

	public setCoverageLock(deviceId: string, enabled: boolean): Observable<void> {
		this.logger.trace('setCoverageLock()', 'Setting coverage lock', { deviceId, enabled });
		return this.updateCoverageDevice(deviceId, [{ id: deviceId, features: { coverageLock: { enabled } } }]);
	}

	public setUserPerspective(deviceId: string, userPerspective: DeviceUserPerspective): Observable<void> {
		this.logger.trace('setUserPerspective()', 'Setting user perspective', { deviceId, userPerspective });
		return this.updateCoverageDevice(deviceId, [{ id: deviceId, features: { userPerspective } }]);
	}

	public updateMeasurementUnit(deviceId: string, measurementUnit: MeasurementUnit): Observable<void> {
		this.logger.trace('updateMeasurementUnit()', 'Setting measurement unit', { deviceId, measurementUnit });
		const unit = mapMeasurementUnitToSysApi(measurementUnit);
		return this.updateUserInterfacePreferencesMutationGQL
			.mutate({
				variables: {
					input: { id: deviceId, unitOfMeasurement: { distance: unit } }
				}
			})
			.pipe(
				map((result) => {
					if (result.data?.updateUserInterfacePreferences?.error) {
						throw new Error(
							result.data.updateUserInterfacePreferences.error.message ??
								'Failed to update measurement unit'
						);
					}
					this.logger.trace('updateMeasurementUnit()', 'Done', { deviceId });
					return;
				}),
				catchError((error) => {
					this.logger.error('updateMeasurementUnit()', 'Error', { error });
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	private updateCoverageDevice(deviceId: string, updates: DeviceUpdateInput[]): Observable<void> {
		const inputs = updates.reduce<NodeUpdateInput[]>(
			(all, next) => [...all, { device: { id: next.id, features: next.features } }],
			[]
		);

		return this.updateCoverageDeviceMutationGQL
			.mutate({ variables: { updates: inputs, injectKeyring: '//supplied-by-keyring-injector' } })
			.pipe(
				map((result) => {
					const errors = result.data?.updateNodes.filter((e) => 'error' in e && e.error !== null) ?? [];
					if (errors?.length > 0) {
						const message = errors
							.map((fail) => ('error' in fail ? (fail.error?.message ?? '') : ''))
							.join('\n');

						throw new Error(message);
					}

					this.logger.trace('updateCoverageDevice()', 'Done', { deviceId, updates });
					return;
				}),
				catchError((error) => {
					this.logger.error('updateCoverageDevice()', 'Failed to update device', {
						deviceId,
						updates,
						error
					});
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	// TODO: remove when DCA901 supports height/rotation properties (DNG-36929).
	private mergeDcaShimmedFeatures$(device: CoverageDevice): Observable<CoverageDevice> {
		if (device.model !== DeviceModel.DCA901) {
			return of(device);
		}
		this.dcaFeatureShim.register(device.id);
		return this.dcaFeatureShim.getState$(device.id).pipe(
			map((state) => {
				return {
					...device,
					features: {
						...device.features,
						height: { value: state.height, isMissing: false },
						rotation: {
							value: {
								xAxis: state.rotations.xAxis ?? 0,
								yAxis: state.rotations.yAxis,
								zAxis: state.rotations.zAxis ?? 0
							},
							isMissing: false
						}
					}
				};
			})
		);
	}
}
